import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Alert,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CalculateRoundedIcon from "@mui/icons-material/CalculateRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import IndividualLiquidationFormDialog from "./IndividualLiquidationFormDialog";
import TaxReceiptsDialog from "./TaxReceiptsDialog";
import {
  getIndividualLiquidationsByLegalFile,
  deleteIndividualLiquidation,
} from "../../services/IndividualLiquidationService";
import { downloadLegalFileDocument } from "../../services/LegalFileAttachmentService";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useTheme } from "@mui/material/styles";
import { formatCurrency } from "../../config/Helper";
import { isLegalFileLocked } from "../LegalFile/Detail/detailHelpers";

const CATEGORY_LABELS = { capital: "Capital", honorarios: "Honorarios", gastos: "Gastos" };
const RATE_LABELS = { pasiva: "Tasa Pasiva", tim: "TIM", banco_cordoba: "Bco. Córdoba" };

function fmtDate(v) {
  if (!v) return "—";
  // "YYYY-MM-DD" se formatea directo para no desfasar un día por timezone.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  if (m) return `${+m[3]}/${+m[2]}/${m[1]}`;
  return new Date(v).toLocaleDateString("es-AR");
}

function MetaPill({ label, value, isDark }) {
  return (
    <Box
      sx={{
        px: 1.6,
        py: 1,
        borderRadius: 2,
        border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.09)",
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.65)",
        minWidth: 100,
      }}
    >
      <Typography sx={{ opacity: 0.5, display: "block", fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 900, fontSize: 13, lineHeight: 1.2 }}>{value}</Typography>
    </Box>
  );
}

// Etiqueta corta de un interés (tasa + %).
const num = (v) => Number(v).toLocaleString("es-AR", { maximumFractionDigits: 2 });

// La Municipalidad pide los rubros de Honorarios en JUS. En esta vista
// (interna, para el operador) se muestra el valor en JUS arriba y el monto
// en pesos real abajo, para ver cuánto suma al total. El PDF en cambio solo
// muestra JUS, sin pesos. El total liquidación siempre suma en pesos.
function JusPesos({ amount, jusValue }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.25 }}>
      <span>
        {(Number(amount) / Number(jusValue)).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} JUS
      </span>
      <span style={{ fontSize: "0.78em", opacity: 0.6, fontWeight: 400 }}>{formatCurrency(amount)}</span>
    </Box>
  );
}

// "TASA PASIVA + 2,00%" / "8,00%" (simple)
function rateDesc(e) {
  const pr = Number(e.plus_rate) || 0;
  if (e.rate_type === "simple") return `${num(pr)}%`;
  const base = (RATE_LABELS[e.rate_type] ?? e.rate_type ?? "—").toUpperCase();
  return pr > 0 ? `${base} + ${num(pr)}%` : base;
}

function CategorySection({ category, items, isDark, jusValue }) {
  if (!items.length) return null;
  const subtotal = items.reduce((s, i) => s + Number(i.base_amount ?? 0) + Number(i.interest_amount ?? 0) + Number(i.punitorio_amount ?? 0), 0);
  const subtotalIva = items.reduce((s, i) => s + Number(i.iva_amount ?? 0), 0);
  const catUp = (CATEGORY_LABELS[category] ?? category).toUpperCase();
  const isHonorarios = category === "honorarios";
  // Cada rubro decide por sí mismo (Por JUS / Monto fijo). El SALDO de
  // Honorarios va en JUS si hay al menos un rubro "Por JUS" en la categoría
  // (aunque se mezcle con monto fijo). Si TODOS son monto fijo, va en pesos —
  // no tiene sentido convertir una planilla 100% a monto fijo.
  const catJusActive = isHonorarios && Number(jusValue) > 0 && items.some((i) => i.amount_mode === "jus_multiplier");
  const f = catJusActive ? (v) => <JusPesos amount={v} jusValue={jusValue} /> : formatCurrency;

  // La discriminación de saldos es UNA sola, al final de la categoría (suma de
  // todos los subrubros). Gastos = un único "SALDO GASTOS" (base + interés).
  // Capital / honorarios = SALDO, SALDO INTERESES y SALDO IVA por separado.
  const combined = category === "gastos";
  const catBase = items.reduce((s, i) => s + Number(i.base_amount ?? 0), 0);
  const catInt = items.reduce((s, i) => s + Number(i.interest_amount ?? 0) + Number(i.punitorio_amount ?? 0), 0);
  const catIva = items.reduce((s, i) => s + Number(i.iva_amount ?? 0), 0);

  const th = {
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    opacity: 0.5,
    py: 0.7,
    borderBottom: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.08)",
  };
  const rowCell = { fontSize: 12, py: 0.85 };
  const subCell = { fontSize: 10.5, py: 0.5, opacity: 0.8, borderBottom: "none" };
  const saldoCell = {
    fontSize: 11.5,
    py: 0.5,
    fontWeight: 900,
    borderBottom: "none",
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.9,
          borderRadius: "8px 8px 0 0",
          background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.025)",
          border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.07)",
          borderBottom: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", opacity: 0.7 }}>{catUp}</Typography>
          <Typography sx={{ fontSize: 10, opacity: 0.45, fontWeight: 600 }}>
            {items.length} {items.length === 1 ? "rubro" : "rubros"}
          </Typography>
          {catJusActive && (
            <Tooltip title="El valor en JUS está redondeado para mostrar. El monto exacto y válido es el que figura en pesos — no multiplicar el JUS redondeado por el valor de JUS para verificar, puede no coincidir.">
              <InfoOutlinedIcon sx={{ fontSize: 13, opacity: 0.4 }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{f(subtotal)}</Typography>
          {subtotalIva > 0 && <Typography sx={{ fontSize: 10, opacity: 0.55 }}>+ IVA {f(subtotalIva)}</Typography>}
        </Box>
      </Box>

      <TableContainer
        sx={{
          border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.1)",
          borderTop: "none",
          borderRadius: "0 0 8px 8px",
        }}
      >
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...th, pl: 1.5, width: "26%" }}>Concepto</TableCell>
              <TableCell sx={{ ...th, width: "9%" }}>Tipo</TableCell>
              <TableCell sx={{ ...th, width: "12%" }}>Desde</TableCell>
              <TableCell sx={{ ...th, width: "12%" }}>Hasta</TableCell>
              <TableCell align="right" sx={{ ...th, width: "12%" }}>Tasa</TableCell>
              <TableCell align="right" sx={{ ...th, width: "14%" }}>Base</TableCell>
              <TableCell align="right" sx={{ ...th, width: "15%" }}>Interés</TableCell>
              <TableCell align="right" sx={{ ...th, width: "14%", pr: 1.5 }}>IVA</TableCell>
            </TableRow>

            {items.map((item, idx) => {
              const interestList =
                Array.isArray(item.interests) && item.interests.length
                  ? item.interests
                  : item.interest_rate_type
                    ? [{ rate_type: item.interest_rate_type, from: item.interest_from, to: item.interest_to, plus_rate: item.interest_plus_rate, plus_period: item.interest_plus_period, amount: item.interest_amount }]
                    : [];
              const metaList = Array.isArray(item?.metadata?.interests)
                ? item.metadata.interests
                : item?.metadata?.interest
                  ? [item.metadata.interest]
                  : [];

              const tipo = item.amount_mode === "jus_multiplier" ? "Por JUS" : "Fijo";
              const base = Number(item.base_amount ?? 0);
              const sumInt = Number(item.interest_amount ?? 0) + Number(item.punitorio_amount ?? 0);
              const ivaAmt = Number(item.iva_amount ?? 0);
              // Por rubro: JUS si este ítem puntual está cargado "Por JUS", pesos si no.
              const itemJusActive = isHonorarios && item.amount_mode === "jus_multiplier" && Number(jusValue) > 0;
              const fi = itemJusActive ? (v) => <JusPesos amount={v} jusValue={jusValue} /> : formatCurrency;

              return (
                <Fragment key={item.id ?? idx}>
                  {/* Fila resumen del rubro */}
                  <TableRow>
                    <TableCell sx={{ ...rowCell, fontWeight: 800, pl: 1.5 }}>{item.concept ?? "—"}</TableCell>
                    <TableCell sx={{ ...rowCell, fontSize: 10, opacity: 0.55, fontWeight: 600 }}>{tipo}</TableCell>
                    <TableCell sx={rowCell} />
                    <TableCell sx={rowCell} />
                    <TableCell sx={rowCell} align="right" />
                    <TableCell sx={{ ...rowCell, fontWeight: 800 }} align="right">{fi(base)}</TableCell>
                    <TableCell sx={{ ...rowCell, fontWeight: 800 }} align="right">{sumInt ? fi(sumInt) : "—"}</TableCell>
                    <TableCell sx={{ ...rowCell, pr: 1.5, opacity: 0.8 }} align="right">{ivaAmt > 0 ? fi(ivaAmt) : "—"}</TableCell>
                  </TableRow>

                  {interestList.map((e, i) => {
                    const m = metaList[i] || {};
                    const isSimple = e.rate_type === "simple";
                    const tasa = isSimple
                      ? `${num(e.plus_rate)}% ${(e.plus_period || "").trim()}`.trim()
                      : Number(m.update_rate) > 0
                        ? `act. ${num(m.update_rate)}%`
                        : "—";
                    return (
                      <TableRow key={`ie-${i}`}>
                        <TableCell colSpan={2} sx={{ ...subCell, pl: 3 }}>
                          ↳ {rateDesc(e)}
                          {m.days != null ? ` · ${m.days} días` : ""}
                        </TableCell>
                        <TableCell sx={subCell}>{fmtDate(e.from)}</TableCell>
                        <TableCell sx={subCell}>{fmtDate(e.to)}</TableCell>
                        <TableCell sx={subCell} align="right">{tasa}</TableCell>
                        <TableCell sx={subCell} align="right">{fi(base)}</TableCell>
                        <TableCell sx={subCell} align="right">{fi(e.amount ?? 0)}</TableCell>
                        <TableCell sx={subCell} />
                      </TableRow>
                    );
                  })}

                  {item.punitorio_rate_type && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ ...subCell, pl: 3 }}>
                        ↳ Punitorios {RATE_LABELS[item.punitorio_rate_type] ?? item.punitorio_rate_type}
                        {Number(item.punitorio_plus_rate) > 0 ? ` + ${num(item.punitorio_plus_rate)}%` : ""}
                      </TableCell>
                      <TableCell sx={subCell}>{fmtDate(item.punitorio_from)}</TableCell>
                      <TableCell sx={subCell}>{fmtDate(item.punitorio_to)}</TableCell>
                      <TableCell sx={subCell} align="right">
                        {Number(item?.metadata?.punitorio?.update_rate) > 0 ? `act. ${num(item.metadata.punitorio.update_rate)}%` : "—"}
                      </TableCell>
                      <TableCell sx={subCell} align="right">{fi(base)}</TableCell>
                      <TableCell sx={subCell} align="right">{fi(item.punitorio_amount ?? 0)}</TableCell>
                      <TableCell sx={subCell} />
                    </TableRow>
                  )}

                  {idx < items.length - 1 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: isDark ? "2px solid rgba(255,255,255,0.09)" : "2px solid rgba(0,0,0,0.08)" }} />
                    </TableRow>
                  )}
                </Fragment>
              );
            })}

            {/* Un único bloque de saldos al final de la categoría (suma de todos los subrubros) */}
            {(() => {
              const topBorder = isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.16)";
              if (combined) {
                return (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ ...saldoCell, borderTop: topBorder }}>SALDO {catUp}</TableCell>
                    <TableCell sx={{ ...saldoCell, borderTop: topBorder, pr: 1.5 }} align="right">{f(catBase + catInt)}</TableCell>
                  </TableRow>
                );
              }
              return (
                <>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ ...saldoCell, borderTop: topBorder }}>SALDO {catUp}</TableCell>
                    <TableCell sx={{ ...saldoCell, borderTop: topBorder }} align="right">{f(catBase)}</TableCell>
                    <TableCell sx={{ ...saldoCell, borderTop: topBorder }} />
                    <TableCell sx={{ ...saldoCell, borderTop: topBorder }} />
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={saldoCell}>SALDO INTERESES {catUp}</TableCell>
                    <TableCell sx={saldoCell} align="right">{f(catInt)}</TableCell>
                    <TableCell sx={saldoCell} />
                  </TableRow>
                  {catIva > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={saldoCell}>SALDO IVA {catUp}</TableCell>
                      <TableCell sx={{ ...saldoCell, pr: 1.5 }} align="right">{f(catIva)}</TableCell>
                    </TableRow>
                  )}
                </>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Totales (barra simple, estilo municipalidad) ────────────────────────────
function TotalsSection({ items, isDark, jusValue }) {
  const sum = (cat) =>
    (items ?? [])
      .filter((i) => i.category === cat)
      .reduce((s, i) => s + Number(i.base_amount ?? 0) + Number(i.interest_amount ?? 0) + Number(i.punitorio_amount ?? 0), 0);
  const ivaOf = (cat) => (items ?? []).filter((i) => i.category === cat).reduce((s, i) => s + Number(i.iva_amount ?? 0), 0);

  const cards = [
    { label: "Capital", subtotal: sum("capital"), iva: ivaOf("capital") },
    { label: "Gastos", subtotal: sum("gastos"), iva: ivaOf("gastos") },
    { label: "Honorarios", subtotal: sum("honorarios"), iva: ivaOf("honorarios") },
  ];
  const totalIva = (items ?? []).reduce((s, i) => s + Number(i.iva_amount ?? 0), 0);
  const grand = cards.reduce((s, c) => s + c.subtotal, 0) + totalIva;
  const honorariosHasJusRubro = (items ?? []).some((i) => i.category === "honorarios" && i.amount_mode === "jus_multiplier");

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        flexWrap: "wrap",
        alignItems: "stretch",
        p: 2,
        borderRadius: 2.5,
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.65)",
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.07)",
      }}
    >
      {cards.map(({ label, subtotal, iva }) => {
        const jusActive = label === "Honorarios" && Number(jusValue) > 0 && honorariosHasJusRubro;
        const f = jusActive ? (v) => <JusPesos amount={v} jusValue={jusValue} /> : formatCurrency;
        return (
          <Box
            key={label}
            sx={{
              flex: "1 1 120px",
              minWidth: 110,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, display: "flex", alignItems: "center", gap: 0.4, mb: 0.3 }}>
              {label}
              {jusActive && (
                <Tooltip title="El valor en JUS está redondeado para mostrar. El monto exacto y válido es el que figura en pesos — no multiplicar el JUS redondeado por el valor de JUS para verificar, puede no coincidir.">
                  <InfoOutlinedIcon sx={{ fontSize: 12, opacity: 0.5 }} />
                </Tooltip>
              )}
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{f(subtotal)}</Typography>
            {iva > 0 && (
              <Box sx={{ mt: 0.6, pt: 0.6, borderTop: isDark ? "1px dashed rgba(255,255,255,0.12)" : "1px dashed rgba(0,0,0,0.12)" }}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.45, display: "block", mb: 0.2 }}>
                  + IVA (21%)
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: 12, opacity: 0.8 }}>{f(iva)}</Typography>
              </Box>
            )}
          </Box>
        );
      })}

      <Box
        sx={{
          ml: "auto",
          px: 2,
          py: 1,
          borderRadius: 2,
          background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-end",
          minWidth: 160,
        }}
      >
        <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.5, mb: 0.3 }}>
          Total liquidación
        </Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 20, color: "primary.main", letterSpacing: -0.5 }}>
          {formatCurrency(grand)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function IndividualLiquidationTab({ legalFile, onActiveChange, onCountChange, onRefreshLegalFile }) {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const blobUrlRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);

  const load = useCallback(async () => {
    if (!legalFile?.id) return;
    setLoading(true);
    setError("");
    try {
      const res = await getIndividualLiquidationsByLegalFile(legalFile.id);
      const activeData = res.data?.active ?? null;
      const hist = res.data?.history ?? [];
      setActive(activeData);
      setHistory(hist);
      onActiveChange?.(!!activeData);
      onCountChange?.((activeData ? 1 : 0) + hist.length);
    } catch {
      setError("No se pudo cargar la liquidación común.");
    } finally {
      setLoading(false);
    }
  }, [legalFile?.id, onActiveChange, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const openDoc = async (documentId, label) => {
    if (!documentId) {
      showToast("Todavía no hay un PDF generado.", "info");
      return;
    }
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewUrl(null);
    setPreviewTitle(label);
    try {
      const response = await downloadLegalFileDocument(documentId);
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      setPreviewUrl(url);
    } catch {
      showToast("No se pudo cargar el PDF.", "error");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const locked = isLegalFileLocked(legalFile);
  const canCreate = hasPermission("crear liquidacion comun") && !locked;
  const canDelete = hasPermission("eliminar liquidacion comun");

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteIndividualLiquidation(deleteTarget.id);
      showToast("Liquidación eliminada correctamente.", "success");
      setDeleteTarget(null);
      load();
      onRefreshLegalFile?.();
    } catch {
      showToast("No se pudo eliminar la liquidación.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!active) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 4,
            bgcolor: isDark ? "rgba(25,118,210,0.12)" : "rgba(25,118,210,0.08)",
            border: "1px solid rgba(25,118,210,0.2)",
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 1.5,
            color: "primary.main",
          }}
        >
          <CalculateRoundedIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Sin liquidación común</Typography>
        <Typography variant="body2" sx={{ opacity: 0.6, mb: 2.5 }}>
          No hay planilla de liquidación común para este expediente.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
          {canCreate && (
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setFormOpen(true)} sx={{ borderRadius: 2.5, fontWeight: 900 }}>
              Crear liquidación común
            </Button>
          )}
          <Button variant="outlined" startIcon={<ReceiptLongRoundedIcon />} onClick={() => setTaxOpen(true)} sx={{ borderRadius: 2.5, fontWeight: 800 }}>
            Comprobantes de tasa
          </Button>
        </Box>
        <IndividualLiquidationFormDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          legalFile={legalFile}
          onSaved={() => {
            load();
            onRefreshLegalFile?.();
            showToast("Liquidación guardada.", "success");
          }}
        />
        <TaxReceiptsDialog
          open={taxOpen}
          onClose={() => setTaxOpen(false)}
          legalFileId={legalFile?.id}
          legalFileCode={legalFile?.code}
        />
      </Box>
    );
  }

  const itemsByCategory = ["capital", "gastos", "honorarios"].map((cat) => ({
    cat,
    items: (active.items ?? []).filter((i) => i.category === cat),
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2, mb: 0.5 }}>
        <Box sx={{ display: "flex", gap: 1.2, alignItems: "center", minWidth: 0 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              background: isDark ? "rgba(25,118,210,0.10)" : "rgba(255,255,255,0.72)",
              border: "1px solid rgba(25,118,210,0.22)",
              color: "primary.main",
              flexShrink: 0,
            }}
          >
            <CalculateRoundedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, fontSize: 18, lineHeight: 1.15 }}>
              Liquidación común activa · v{active.version}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Planilla de liquidación común del expediente.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title="Comprobantes de tasa (portal judicial)">
            <IconButton size="small" onClick={() => setTaxOpen(true)} sx={{ borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}>
              <ReceiptLongRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver PDF de la planilla">
            <IconButton size="small" onClick={() => openDoc(active.legal_file_document_id, `Planilla v${active.version}`)} sx={{ borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}>
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canCreate && (
            <Tooltip title="Nueva versión">
              <IconButton size="small" onClick={() => setFormOpen(true)} sx={{ borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" }}>
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Eliminar esta versión">
              <IconButton size="small" onClick={() => setDeleteTarget(active)} sx={{ borderRadius: 2, color: "error.main", bgcolor: isDark ? "rgba(211,47,47,0.08)" : "rgba(211,47,47,0.05)" }}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {history.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", px: 1.5, py: 1, borderRadius: 2, background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.06)" }}>
          <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, opacity: 0.4, mr: 0.5 }}>
            Versiones anteriores
          </Typography>
          {history.map((liq) => (
            <Box key={liq.id} sx={{ display: "flex", alignItems: "center", gap: 0.4, px: 1.2, py: 0.45, borderRadius: 1.5, background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.75)", border: isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.09)" }}>
              <Box onClick={() => openDoc(liq.legal_file_document_id, `Planilla v${liq.version}`)} sx={{ display: "flex", alignItems: "center", gap: 0.8, cursor: "pointer", "&:hover": { opacity: 0.75 } }}>
                <VisibilityRoundedIcon sx={{ fontSize: 13, opacity: 0.5 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>v{liq.version}</Typography>
                <Typography sx={{ fontSize: 10, opacity: 0.45 }}>{fmtDate(liq.created_at)}</Typography>
              </Box>
              {canDelete && (
                <IconButton size="small" onClick={() => setDeleteTarget(liq)} sx={{ p: 0.3, ml: 0.4, color: "error.main", opacity: 0.6, "&:hover": { opacity: 1 } }}>
                  <DeleteRoundedIcon sx={{ fontSize: 13 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <MetaPill label="Juzgado" value={legalFile?.dependencies || "—"} isDark={isDark} />
        <MetaPill label="Fecha de cálculo" value={fmtDate(active.calculation_date)} isDark={isDark} />
        <MetaPill label="Valor JUS" value={active.jus_value ? formatCurrency(active.jus_value) : "—"} isDark={isDark} />
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {itemsByCategory.map(({ cat, items }) => (
          <CategorySection
            key={cat}
            category={cat}
            items={items}
            isDark={isDark}
            jusValue={active.jus_value}
          />
        ))}
      </Box>

      <TotalsSection
        items={active.items ?? []}
        isDark={isDark}
        jusValue={active.jus_value}
      />

      {active.notes && (
        <Box sx={{ px: 2, py: 1.2, borderRadius: 2, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.6)", border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)" }}>
          <Typography sx={{ opacity: 0.5, fontWeight: 700, display: "block", fontSize: 9, textTransform: "uppercase", letterSpacing: 0.6, mb: 0.5 }}>
            Notas
          </Typography>
          <Typography variant="body2">{active.notes}</Typography>
        </Box>
      )}

      <IndividualLiquidationFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        legalFile={legalFile}
        editingLiquidation={active}
        onSaved={() => {
          load();
          showToast("Liquidación guardada.", "success");
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleteLoading}
        title="Eliminar liquidación"
        description={`¿Eliminar la versión ${deleteTarget?.version ?? ""}? Se borrarán los ítems y los PDF generados. Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        confirmColor="error"
      />

      <AppDialog open={previewOpen} onClose={handleClosePreview} title={`PDF · ${previewTitle}`} maxWidth="xl" fullWidth stickyHeader contentSx={{ p: 0 }}>
        {previewLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : previewUrl ? (
          <iframe src={previewUrl} style={{ width: "100%", height: "78vh", border: 0, display: "block" }} title="preview" />
        ) : null}
      </AppDialog>

      <TaxReceiptsDialog
        open={taxOpen}
        onClose={() => setTaxOpen(false)}
        legalFileId={legalFile?.id}
        legalFileCode={legalFile?.code}
      />
    </Box>
  );
}
